import { useMemo, useState } from "react";
import { useStudents } from "@/hooks/useStudents";
import { Student } from "@/types";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { geocodeAddress } from "@/lib/vworld";
import { reviewAddressWithAi } from "@/lib/addressReview";
import { deriveBuildingName } from "@/lib/excelParse";

import { getRegionConfig } from "@/lib/region";
import { plainError } from "@/lib/plainError";
/**
 * AI 가 자동 수정하지 못한 주소를 관리자가 수동으로 검토·수정하는 페이지.
 *
 * 각 행마다:
 *   - 원본 주소 + 학교 + 구/동
 *   - AI 제시 후보 (도로명 / 지번)
 *   - AI 추론 설명 + 가정
 *   - 수동 입력창 + 검증 버튼 (지오코딩 시도)
 *   - 승인 / 건너뛰기
 */
export default function AddressReview() {
  const { students, loading } = useStudents();
  const [processing, setProcessing] = useState<Record<string, boolean>>({});

  const pending = useMemo(
    () =>
      students.filter(
        (s) => s.geocodeStatus === "needs_review" || s.geocodeStatus === "failed",
      ),
    [students],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">⚠️ 주소 검토</h1>
        <div className="text-sm text-slate-500">
          AI 가 확신하지 못한 주소들. 수동 확인 또는 수정 필요.
        </div>
      </div>

      {loading && <div className="text-slate-400">로딩…</div>}
      {!loading && pending.length === 0 && (
        <div className="card p-8 text-center text-slate-400">
          검토가 필요한 주소가 없습니다 ✅
        </div>
      )}

      <div className="space-y-3">
        {pending.map((s) => (
          <ReviewRow
            key={s.id}
            student={s}
            busy={!!processing[s.id]}
            setBusy={(v) => setProcessing((p) => ({ ...p, [s.id]: v }))}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewRow({
  student,
  busy,
  setBusy,
}: {
  student: Student;
  busy: boolean;
  setBusy: (v: boolean) => void;
}) {
  const [manualAddress, setManualAddress] = useState("");
  const [verifyResult, setVerifyResult] = useState<
    { ok: true; lat: number; lng: number; matched: string } | { ok: false; msg: string } | null
  >(null);

  const originalAddress =
    student.geocodeOriginalAddress ||
    student.refinedAddress ||
    student.rawAddress;

  const applyCandidate = async (
    address: string,
    lat: number,
    lng: number,
    reasoning: string,
  ) => {
    setBusy(true);
    try {
      const bn = deriveBuildingName(address);
      await updateDoc(doc(db, "students", student.id), {
        refinedAddress: address,
        ...(bn ? { buildingName: bn } : {}),
        lat,
        lng,
        geocodedAt: new Date(),
        geocodeStatus: "ok",
        geocodeNeedsCheck: false,
        geocodeReasoning: reasoning,
        geocodeOriginalAddress: originalAddress,
        geocodeCandidates: [],
      });
    } finally {
      setBusy(false);
    }
  };

  const verifyManual = async () => {
    const q = manualAddress.trim();
    if (!q) return;
    setBusy(true);
    setVerifyResult(null);
    try {
      const p = await geocodeAddress(q);
      if (p) {
        setVerifyResult({ ok: true, lat: p.y, lng: p.x, matched: p.matched });
      } else {
        setVerifyResult({ ok: false, msg: "해당 주소로 지도 위치를 찾지 못했습니다." });
      }
    } catch (e: any) {
      setVerifyResult({ ok: false, msg: plainError(e, "지도에서 위치를 못 찾았습니다.") });
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    if (!verifyResult || !verifyResult.ok) return;
    await applyCandidate(
      verifyResult.matched || manualAddress.trim(),
      verifyResult.lat,
      verifyResult.lng,
      "관리자 수동 입력",
    );
  };

  const retryAi = async () => {
    setBusy(true);
    try {
      const result = await reviewAddressWithAi({
        rawAddress: student.rawAddress,
        refinedAddress: student.refinedAddress,
        school: student.school,
        gu: student.gu,
        dong: student.dong,
        buildingName: student.buildingName,
      });
      if (result.status === "ai_fixed" && result.fixed) {
        await updateDoc(doc(db, "students", student.id), {
          refinedAddress: result.fixed.address,
          lat: result.fixed.lat,
          lng: result.fixed.lng,
          geocodedAt: new Date(),
          geocodeStatus: "ai_fixed",
          geocodeNeedsCheck: true,
          geocodeReasoning: result.fixed.reasoning,
          geocodeOriginalAddress: result.fixed.original,
          geocodeCandidates: [],
        });
      } else {
        await updateDoc(doc(db, "students", student.id), {
          geocodeStatus: result.status,
          geocodeCandidates: result.candidates || [],
          geocodeReasoning: result.aiReasoning || "",
          geocodeAssumptions: result.assumptions || [],
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      // 검토 보류 — courier 로 돌림 (월말 택배 체크)
      await updateDoc(doc(db, "students", student.id), {
        courierFlag: true,
        courierReason: "always", // 주소를 못 찾은 집은 새 달에도 택배 — 주소가 고쳐지면 그때 푼다
        geocodeStatus: "failed",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-bold">
            {student.school} · {student.name}
            {student.guardian && <span className="text-slate-500 font-normal"> (보호자 {student.guardian})</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            등록된 구/동: {student.gu || "—"} / {student.dong || "—"} · {student.phone}
          </div>
        </div>
        <div className="text-xs">
          {student.geocodeStatus === "failed" ? (
            <span className="px-2 py-1 rounded bg-rose-100 text-rose-800">AI 호출 실패</span>
          ) : (
            <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">AI 확신 못 함</span>
          )}
        </div>
      </div>

      <div className="text-sm">
        <div className="text-slate-500 text-xs">원본 주소</div>
        <div className="font-mono text-slate-800">{originalAddress || "(주소 없음)"}</div>
      </div>

      {student.geocodeReasoning && (
        <div className="text-xs bg-slate-50 rounded p-2 border border-slate-200">
          <div className="font-semibold text-slate-600 mb-1">🤖 AI 분석</div>
          <div className="text-slate-700">{student.geocodeReasoning}</div>
          {student.geocodeAssumptions && student.geocodeAssumptions.length > 0 && (
            <ul className="list-disc list-inside mt-1 text-slate-500">
              {student.geocodeAssumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
        </div>
      )}

      {student.geocodeCandidates && student.geocodeCandidates.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">AI 제시 후보</div>
          {student.geocodeCandidates.map((c, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-sm bg-white border rounded p-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.type === "ROAD" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}>
                    {c.type === "ROAD" ? "도로명" : "지번"}
                  </span>
                  <span className="text-[10px] text-slate-500">신뢰도 {c.confidence}</span>
                  {c.lat && c.lng && (
                    <span className="text-[10px] text-emerald-600">✓ 지도 위치 찾음</span>
                  )}
                </div>
                <div className="font-mono text-slate-800 mt-1 truncate">{c.address}</div>
              </div>
              {c.lat && c.lng ? (
                <button
                  className="btn-primary text-xs whitespace-nowrap"
                  disabled={busy}
                  onClick={() => applyCandidate(c.address, c.lat!, c.lng!, c.reasoning || "AI 후보 수락")}
                >
                  이걸로 적용
                </button>
              ) : (
                <span className="text-[10px] text-rose-500 whitespace-nowrap">지도 위치 못 찾음</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="border-t pt-3 space-y-2">
        <div className="text-xs font-semibold text-slate-600">직접 입력</div>
        <div className="flex gap-2">
          <input
            className="input flex-1 text-sm"
            placeholder={`예: ${getRegionConfig().sido} ○○구 ○○로 12`}
            value={manualAddress}
            onChange={(e) => {
              setManualAddress(e.target.value);
              setVerifyResult(null);
            }}
          />
          <button className="btn-secondary text-xs whitespace-nowrap" disabled={busy || !manualAddress.trim()} onClick={verifyManual}>
            검증
          </button>
        </div>
        {verifyResult && verifyResult.ok && (
          <div className="flex items-center justify-between gap-3 text-xs bg-emerald-50 rounded p-2 border border-emerald-200">
            <div className="text-emerald-800">
              ✓ 매칭: <span className="font-mono">{verifyResult.matched}</span>
              <span className="text-slate-500"> ({verifyResult.lat.toFixed(4)}, {verifyResult.lng.toFixed(4)})</span>
            </div>
            <button className="btn-primary text-xs whitespace-nowrap" disabled={busy} onClick={saveManual}>
              저장
            </button>
          </div>
        )}
        {verifyResult && !verifyResult.ok && (
          <div className="text-xs text-rose-600">{verifyResult.msg}</div>
        )}
      </div>

      <div className="flex gap-2 pt-2 border-t">
        <button className="btn-ghost text-xs" disabled={busy} onClick={retryAi}>
          🔄 AI 재시도
        </button>
        <button className="btn-ghost text-xs text-rose-600" disabled={busy} onClick={skip}>
          보류 → 월말 택배로 돌림
        </button>
      </div>
    </div>
  );
}
